import { useTranslation } from 'react-i18next';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import { SSHKey } from '../utils/types';

interface SSHMobileKeyCardActionsProps {
  sshKey: SSHKey;
  reAddingKeyId?: number | null;
  onView: (id: number) => void;
  onDelete: (id: number) => void;
  onReAdd?: (id: number) => void;
  onRemove?: (id: number) => void;
  mobileMissingActions?: boolean;
  isServerOnline?: boolean;
}

const removeButtonSx = {
  color: 'error.main',
  textTransform: 'none',
  '&:hover': {
    bgcolor: 'action.hover',
  },
};

const SSHMobileKeyCardActions = ({
  sshKey,
  reAddingKeyId = null,
  onView,
  onDelete,
  onReAdd = undefined,
  onRemove = undefined,
  mobileMissingActions = false,
  isServerOnline = true,
}: SSHMobileKeyCardActionsProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'sshSettings',
  });

  const isMissingFromServer = sshKey.status === 'notOnServer';
  const isReAddLoading = reAddingKeyId === sshKey.id;

  if (mobileMissingActions) {
    if (!isMissingFromServer) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        {onReAdd && (
          <LoadingButton
            variant="outlined"
            size="small"
            loading={isReAddLoading}
            disabled={!isServerOnline}
            startIcon={<LoopOutlinedIcon fontSize="small" />}
            onClick={() => onReAdd(sshKey.id)}
          >
            {t('actions.reAdd')}
          </LoadingButton>
        )}

        {onRemove && (
          <Button
            variant="text"
            color="error"
            size="small"
            sx={removeButtonSx}
            onClick={() => onRemove(sshKey.id)}
            disabled={!isServerOnline}
          >
            {t('actions.remove')}
          </Button>
        )}
      </Stack>
    );
  }

  if (isMissingFromServer) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <IconButton
        size="small"
        aria-label={t('actions.view')}
        onClick={() => onView(sshKey.id)}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        aria-label={t('actions.delete')}
        onClick={() => onDelete(sshKey.id)}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

export default SSHMobileKeyCardActions;
